// Websocket listener for the server.
// Re-emits every server message on the app event bus, keyed by message type.

import eventEmitter from 'events/EventEmitter';

// The server authorises the HTTP upgrade itself and rejects it with 401 when no
// token is present. Browsers cannot set headers on a websocket handshake, so the
// token travels as a query parameter.
const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30000;
// A handshake rejected for a bad token is indistinguishable from a dropped
// connection in the browser, so retrying is capped instead of endless.
const RECONNECT_MAX_ATTEMPTS = 10;

let socket = null;
let reconnectTimer = null;
let reconnectAttempts = 0;
let activeToken = null;

const buildUrl = (token) => {
  const url = new URL(process.env.REACT_APP_API_WS_URL);
  url.searchParams.set('token', token);
  return url.toString();
};

const scheduleReconnect = () => {
  if (!activeToken || reconnectTimer) return;
  if (reconnectAttempts >= RECONNECT_MAX_ATTEMPTS) return;

  const delay = Math.min(
    RECONNECT_BASE_DELAY_MS * 2 ** reconnectAttempts,
    RECONNECT_MAX_DELAY_MS
  );
  reconnectAttempts += 1;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    openSocket();
  }, delay);
};

function openSocket() {
  socket = new WebSocket(buildUrl(activeToken));

  socket.onopen = () => {
    reconnectAttempts = 0;
  };

  socket.onmessage = (event) => {
    const data = JSON.parse(event.data);
    eventEmitter.emit(data.type, data);
  };

  // The server pings every 30s and terminates sockets that stop answering, so a
  // dropped connection must be reopened or live updates stop for the session.
  socket.onclose = () => {
    socket = null;
    scheduleReconnect();
  };
}

/**
 * Opens the authenticated websocket. Safe to call again with the same token.
 *
 * @param {string} token access token issued at login
 */
export function connectWebSocket(token) {
  if (!token) return;
  if (activeToken === token && socket) return;

  disconnectWebSocket();
  activeToken = token;
  reconnectAttempts = 0;
  openSocket();
}

/** Closes the websocket and stops any pending reconnection. */
export function disconnectWebSocket() {
  activeToken = null;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (socket) {
    socket.onclose = null;
    socket.close();
    socket = null;
  }
}
